import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { AlertDismissButton } from '@/components/DismissibleAlert';
import { hasUnresolvedPlaceholder, readApiError } from '@/lib/errors/readApiError';
import type { InlineError } from '@/lib/errors/inlineError';

interface ApiErrorAlertProps {
  /**
   * The caught rejection itself, not a string pulled out of it. Passing the raw value is the
   * point: the component reads it through the one reader, so a screen cannot accidentally
   * render an untranslated key, an English RFC 7807 title, or `String(e)`.
   *
   * Falsy renders nothing, so callers can pass their error state directly without a guard.
   */
  error: unknown;
  /** i18n key for the sentence to show when the response carried no reason of its own. */
  fallbackKey?: string;
  /**
   * Retry affordance. Deliberately suppressed for an authorization refusal: a denied write
   * fails identically on every attempt, so offering "try again" for one is not merely useless
   * but actively misleading about why it failed.
   */
  onRetry?: () => void;
  /** Close affordance for a page-level alert the user may clear without retrying. */
  onDismiss?: () => void;
  className?: string;
  /** Typography override for the message row (e.g. `text-xs` for a compact per-row alert). */
  descriptionClassName?: string;
}

/**
 * The shared way a refused request is shown inline (PBI #1739 Faz 2, AB#1767).
 *
 * Form submission errors belong here rather than in a toast: an ephemeral notification is the
 * wrong channel for a failure the user is expected to act on, because it is gone by the time
 * they look back at the field. Actions with nowhere to put an inline message declare
 * `errorToast` on their mutation instead — see `lib/query/mutationErrorChannel.ts`.
 */
export function ApiErrorAlert({
  error,
  fallbackKey,
  onRetry,
  onDismiss,
  className,
  descriptionClassName,
}: ApiErrorAlertProps) {
  const { t } = useTranslation();
  if (!error) return null;

  const { message, isAuthorization } = readApiError(error, fallbackKey);

  return (
    <Alert variant="destructive" className={cn(className)}>
      <AlertDescription className={cn('flex flex-wrap items-center justify-between gap-2', descriptionClassName)}>
        <span className="min-w-0 wrap-break-word">{message}</span>
        {((onRetry && !isAuthorization) || onDismiss) && (
          <span className="flex shrink-0 items-center gap-2">
            {onRetry && !isAuthorization && (
              <Button type="button" variant="outline" size="sm" onClick={onRetry}>
                {t('common.actions.retry')}
              </Button>
            )}
            {onDismiss && <AlertDismissButton onDismiss={onDismiss} />}
          </span>
        )}
      </AlertDescription>
    </Alert>
  );
}

interface InlineErrorAlertProps {
  value: InlineError | null | undefined;
  /** Honoured for `api` values only; a local message has nothing to retry. */
  onRetry?: () => void;
  onDismiss?: () => void;
  className?: string;
  descriptionClassName?: string;
}

/** Renders a `setState` slot built with `apiError` / `localError` (see `lib/errors/inlineError`). */
export function InlineErrorAlert({ value, onRetry, onDismiss, className, descriptionClassName }: InlineErrorAlertProps) {
  const { t } = useTranslation();
  if (!value) return null;

  if (value.kind === 'api') {
    return (
      <ApiErrorAlert
        error={value.error}
        fallbackKey={value.fallbackKey}
        onRetry={onRetry}
        onDismiss={onDismiss}
        className={className}
        descriptionClassName={descriptionClassName}
      />
    );
  }

  const translated = t(value.key, value.params);
  const text = hasUnresolvedPlaceholder(translated) ? t('errors.generic.unexpected') : translated;
  return (
    <Alert variant="destructive" className={className}>
      <AlertDescription className={cn('flex flex-wrap items-center justify-between gap-2', descriptionClassName)}>
        <span className="min-w-0 wrap-break-word">{text}</span>
        {onDismiss && <AlertDismissButton onDismiss={onDismiss} />}
      </AlertDescription>
    </Alert>
  );
}
